import { memo } from "react";
import { BaseRedoInputContainer } from "../arbiter-components/input/base-redo-input-container";
import { BaseRedoInput } from "../arbiter-components/input/base-redo-text-input";
import BracketIcon from "../arbiter-icon/brackets-ellipses_filled.svg";
import ColorsIcon from "../arbiter-icon/colors.svg";
import EmojiIcon from "../arbiter-icon/face-smile.svg";
import LinkIcon from "../arbiter-icon/link-01_filled.svg";
import MinusIcon from "../arbiter-icon/minus.svg";
import PlusIcon from "../arbiter-icon/plus.svg";
import { Flex } from "../flex";
import AlignCenterIcon from "../icon-old/align-center.svg";
import AlignLeftIcon from "../icon-old/align-left.svg";
import AlignRightIcon from "../icon-old/align-right.svg";
import BoldIcon from "../icon-old/bold.svg";
import ItalicIcon from "../icon-old/italic.svg";
import ListIcon from "../icon-old/list.svg";
import StrikethroughIcon from "../icon-old/strikethrough.svg";
import UnderlineIcon from "../icon-old/underline.svg";
import * as richTextEditorCss from "./forge-quill-editor.module.css";

export interface FontSize {
  label: string;
  value: string;
}

export const FormQuillEditorToolbar = memo(function FormQuillEditorToolbar({
  open,
  id = "toolbar",
  includeDynamicVariables = false,
  fontSize,
  setFontSize,
}: {
  open: boolean;
  id?: string;
  includeDynamicVariables?: boolean;
  fontSize: number;
  setFontSize: (fontSize: number) => void;
}) {
  return (
    <Flex
      className={richTextEditorCss.textFormattingToolbar}
      gap="none"
      id={id}
      onMouseDown={(e) => e.stopPropagation()}
      style={{ visibility: open ? "visible" : "hidden" }}
    >
      {/* inline style works with Quill's magic much better than external CSS */}
      {/* The id="toolbar" is required for Quill to detect this toolbar */}
      <Flex
        align="center"
        borderColor="primary"
        borderRightWidth="1px"
        borderStyle="solid"
        gap="xs"
        px="md"
        py="xs"
      >
        <Flex
          align="center"
          className={richTextEditorCss.fontSizeButton}
          onClick={() => {
            setFontSize(Math.max(6, fontSize - 1));
          }}
        >
          <MinusIcon height={16} width={16} />
        </Flex>
        <BaseRedoInputContainer>
          <BaseRedoInput
            constrainToInputLength
            htmlPattern="^[0-9]+$"
            setValue={(value) => {
              setFontSize(Number(value) || fontSize);
            }}
            value={fontSize.toString()}
          />
        </BaseRedoInputContainer>
        <Flex
          align="center"
          className={richTextEditorCss.fontSizeButton}
          onClick={() => {
            setFontSize(Math.min(72, fontSize + 1));
          }}
        >
          <PlusIcon height={16} width={16} />
        </Flex>
      </Flex>
      <div className={richTextEditorCss.textToolbarOption}>
        <BoldIcon />
        <button className="ql-bold" />
      </div>
      <div className={richTextEditorCss.textToolbarOption}>
        <ItalicIcon />
        <button className="ql-italic" />
      </div>
      <div className={richTextEditorCss.textToolbarOption}>
        <UnderlineIcon />
        <button className="ql-underline" />
      </div>
      <div className={richTextEditorCss.textToolbarOption}>
        <StrikethroughIcon />
        <button className="ql-strike" />
      </div>
      <div className={richTextEditorCss.textToolbarOption}>
        <AlignLeftIcon />
        <button className="ql-align" value="" />
      </div>
      <div className={richTextEditorCss.textToolbarOption}>
        <AlignCenterIcon />
        <button className="ql-align" value="center" />
      </div>
      <div className={richTextEditorCss.textToolbarOption}>
        <AlignRightIcon />
        <button className="ql-align" value="right" />
      </div>
      <div className={richTextEditorCss.textToolbarOption}>
        <ListIcon />
        <button className="ql-list" value="bullet" />
      </div>
      <div className={richTextEditorCss.textToolbarOption}>
        <LinkIcon />
        <button className="rql-link" />
      </div>
      <div className={richTextEditorCss.textToolbarOption}>
        <ColorsIcon />
        <button className="rql-color" />
      </div>
      {includeDynamicVariables && (
        <div className={richTextEditorCss.textToolbarOption}>
          <BracketIcon />
          <button className="rql-dynamic-variable" />
        </div>
      )}
      <div className={richTextEditorCss.textToolbarOption}>
        <EmojiIcon />
        <button className="rql-emoji" />
      </div>
    </Flex>
  );
});
